import Box from '@mui/material/Box';
import DailyImageryIcon from '@apps-common/ui-icons/DailyImagery';
import Divider from '@mui/material/Divider';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PropTypes from 'prop-types';
import React, {Fragment, useEffect} from 'react';
import Typography from '@mui/material/Typography';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';

import ActionsMenu from './actions-menu/index.jsx';
import DailyResultsPanel from './results-panel/index.jsx';
import FiltersPanel from './filters/index.jsx';
import LeftDrawerHeader from '../left-drawer-header.jsx';
import {DRAWERS, TILE_MODE, TOUR_STAGES} from '@explorer/constants';
import {EmptyMessageContainer} from '../../ui/message-container.jsx';
import {LeftDrawer} from '../drawers.jsx';
import {fitExtent} from '@explorer/actions/map';
import {focusItem} from '@explorer/actions/focused-item';
import {selectFilteredResultsList} from '@explorer/selectors/results';
import {setActiveResultId} from '@explorer/actions/sidebar';
import {setTileMode} from '@explorer/actions/tiles';
import {showLeftDrawer} from '@explorer/actions/ui';

const Sidebar = ({
  activeResultId,
  mode,
  focusItem,
  results,
  resultsLoading,
  setActiveResultId,
  tileMode,
  setTileMode,
  open,
  fitExtent,
  ...props
}) => {
  const {t} = useTranslation();

  const getDailyModePanels = () => {
    if (!resultsLoading) {
      if (!props.geometry) {
        return (
          <EmptyMessageContainer
            icon={<LocationOnOutlinedIcon fontSize="inherit" />}
          >
            <Typography variant="h6">
              {t(
                'search.chooseLocationMessage',
                'Choose a location to search through our catalogue of daily imagery.'
              )}
            </Typography>
          </EmptyMessageContainer>
        );
      } else if (props.geometryTooLarge) {
        return (
          <EmptyMessageContainer>
            <LocationOnOutlinedIcon fontSize="large" color="inherit" />
            <Typography variant="h6">
              {t(
                'search.drawSmallerGeoMessage',
                'Draw a smaller AOI to search through our catalogue of daily imagery.'
              )}
            </Typography>
          </EmptyMessageContainer>
        );
      }
    }

    return <DailyResultsPanel results={results} />;
  };

  // ToDo: should this even be here?
  useEffect(() => {
    if (
      (activeResultId && resultsLoading && results.length === 0) ||
      tileMode !== TILE_MODE.RESULTS
    ) {
      setActiveResultId(null);
      focusItem(null);
    }
  }, [
    activeResultId,
    mode,
    results,
    resultsLoading,
    setActiveResultId,
    focusItem,
    tileMode,
  ]);

  useEffect(() => {
    if (open) {
      if (tileMode !== TILE_MODE.RESULTS) {
        setTileMode(TILE_MODE.RESULTS);
      }
    }
  }, [open, setTileMode, tileMode]);

  return (
    <LeftDrawer
      PaperProps={{
        'data-cy': 'sidebar',
        'data-tour': TOUR_STAGES.SEARCH_SIDEBAR,
      }}
      open={open}
    >
      <Fragment>
        {props.showFiltersPanel && <FiltersPanel />}

        {!props.showFiltersPanel && (
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            data-cy="sidebar-content"
          >
            <LeftDrawerHeader
              title={t('search.titleScenes')}
              icon={
                <DailyImageryIcon
                  sx={{color: theme => theme.palette.grey[500]}}
                />
              }
            />
            <Box
              px={2}
              py={1}
              sx={{backgroundColor: theme => theme.palette.grey[100]}}
            >
              <ActionsMenu />
            </Box>
            <Divider />
            {getDailyModePanels()}
          </Box>
        )}
      </Fragment>
    </LeftDrawer>
  );
};

Sidebar.propTypes = {
  activeResultId: PropTypes.string,
  focusItem: PropTypes.func,
  mode: PropTypes.string.isRequired,
  open: PropTypes.bool,
  results: PropTypes.array,
  resultsLoading: PropTypes.bool,
  setActiveResultId: PropTypes.func,
  showFiltersPanel: PropTypes.bool,
  showLeftDrawer: PropTypes.func,
  geometry: PropTypes.object,
  geometryTooLarge: PropTypes.bool,
  setTileMode: PropTypes.func,
  tileMode: PropTypes.string,
  fitExtent: PropTypes.func,
  searchName: PropTypes.string,
};

const mapStateToProps = state => ({
  activeResultId: state.sidebar.activeResultId,
  mode: state.ui.mode,
  resultsLoading: state.results.loading,
  showFiltersPanel: state.sidebar.showFiltersPanel,
  open: state.ui.drawers.left === DRAWERS.SEARCH,
  geometry: state.search.geometry,
  searchName: state.search.name,
  geometryTooLarge: state.search.geometryTooLarge,
  tileMode: state.tiles.mode,
  results: selectFilteredResultsList(state),
});

const mapDispatchProps = {
  focusItem,
  setActiveResultId,
  showLeftDrawer,
  setTileMode,
  fitExtent,
};

export default connect(mapStateToProps, mapDispatchProps)(Sidebar);
